/**
 * Prompt Enhancement System Prompt Generator
 * ============================================
 * Auto-generates system prompts for LLM-based prompt enhancement.
 * 
 * This function creates consistent, high-quality system prompts for any AI model
 * based on module metadata and model-specific constraints.
 * 
 * Usage:
 *   const systemPrompt = generateSystemPrompt('Flux 2 Pro', 'text-to-image generation');
 */

/**
 * Generate a system prompt for prompt enhancement
 * 
 * @param {string} modelName - Name of the AI model (e.g., 'Flux 2 Pro', 'FAL Inpaint')
 * @param {string} modelPurpose - What the model does (e.g., 'text-to-image generation', 'inpainting')
 * @param {Object} constraints - Model-specific constraints
 * @param {string} [constraints.language] - Language restriction (e.g., 'English')
 * @param {boolean} [constraints.noSpecialChars] - Whether to avoid special characters
 * @param {number} [constraints.maxLength] - Maximum prompt length in characters
 * @param {string[]} [constraints.focusAreas] - Specific areas to emphasize
 * @returns {string} System prompt for the LLM
 */
export function generateSystemPrompt(modelName, modelPurpose, constraints = {}) {
  const {
    language = null,
    noSpecialChars = false,
    maxLength = null,
    focusAreas = []
  } = constraints;

  // Build constraint section
  const constraintLines = [];
  if (language) {
    constraintLines.push(`- Use ${language} only`);
  }
  if (noSpecialChars) {
    constraintLines.push(`- Avoid special characters and punctuation`);
  }
  if (maxLength) {
    constraintLines.push(`- Keep the enhanced prompt under ${maxLength} characters`);
  }

  const constraintSection = constraintLines.length > 0 
    ? `\n\nConstraints:\n${constraintLines.join('\n')}`
    : '';

  // Build focus areas section
  const defaultFocusAreas = [
    'Technical details (lighting, composition, materials, camera angles)',
    'Artistic style and mood',
    'Quality keywords (hyper-detailed, sharp focus, premium render)',
    'Specific attributes that enhance realism or artistic impact'
  ];

  const finalFocusAreas = focusAreas.length > 0 ? focusAreas : defaultFocusAreas;
  const focusSection = finalFocusAreas.map(area => `- ${area}`).join('\n');

  // Assemble the complete system prompt
  return `You are an expert AI prompt engineer specializing in ${modelName} for ${modelPurpose}.

Your task: Enhance the user's prompt to maximize output quality while preserving their creative intent and core concept.

Focus on:
${focusSection}

CRITICAL: The user's original vision must remain intact. Only ADD helpful details and technical specificity. Do not change the fundamental concept, subject, or mood.${constraintSection}

Output format: Return ONLY the enhanced prompt text with added details woven in naturally. No explanations, no meta-commentary, no preamble. Just the enhanced prompt itself.`;
}

/**
 * Generate a system prompt for a specific use case
 * 
 * @param {string} useCase - Predefined use case ('text-to-image', 'inpainting', 'outpainting', 'upscaling')
 * @param {Object} options - Additional options
 * @returns {string} System prompt
 */
export function generateSystemPromptByUseCase(useCase, options = {}) {
  const useCaseConfigs = {
    'text-to-image': {
      modelName: options.modelName || 'the AI image generation model',
      modelPurpose: 'creating images from text descriptions',
      focusAreas: [
        'Subject description (pose, expression, anatomy, details)',
        'Lighting setup (studio lighting, natural light, rim light, highlights)',
        'Composition and framing (camera angle, depth of field, rule of thirds)',
        'Materials and textures (glossy, matte, reflective, translucent)',
        'Artistic style (realism, 3D render, concept art, photography)',
        'Technical quality (resolution, sharpness, detail level, post-processing)',
        'Environment and background (setting, atmosphere, color palette)'
      ]
    },
    'inpainting': {
      modelName: options.modelName || 'the AI inpainting model',
      modelPurpose: 'filling masked areas with AI-generated content',
      focusAreas: [
        'Object or element to fill in the masked area',
        'How it should integrate with the existing image',
        'Style consistency with the surrounding context',
        'Specific details about the object (size, color, texture, lighting)',
        'Spatial relationships (placement, scale, perspective)'
      ]
    },
    'outpainting': {
      modelName: options.modelName || 'the AI outpainting model',
      modelPurpose: 'extending images beyond their original boundaries',
      focusAreas: [
        'What should appear in the expanded areas',
        'Environmental context (background, surroundings, setting)',
        'Maintaining visual coherence with the existing image',
        'Lighting and atmosphere consistency',
        'Spatial continuation (how the scene extends naturally)'
      ]
    },
    'upscaling': {
      modelName: options.modelName || 'the AI upscaling model',
      modelPurpose: 'enhancing image resolution and detail',
      focusAreas: [
        'Areas that need enhancement (faces, textures, fine details)',
        'Desired quality improvements (sharpness, clarity, detail)',
        'Style preservation (maintain original artistic intent)',
        'Specific features to enhance or preserve'
      ]
    },
    'image-to-image': {
      modelName: options.modelName || 'the AI image-to-image model',
      modelPurpose: 'generating images with style references from input images',
      focusAreas: [
        'Style transfer from reference images (color palette, lighting, texture, artistic style)',
        'Maintaining reference image characteristics while applying the prompt',
        'Blending style elements from references with prompt description',
        'Coherence between reference images and generated content',
        'Subject description from prompt (what to generate)',
        'How the prompt content should adopt the style of references',
        'Technical quality and detail level matching reference images'
      ]
    }
  };

  const config = useCaseConfigs[useCase];
  if (!config) {
    throw new Error(`Unknown use case: ${useCase}`);
  }

  return generateSystemPrompt(
    config.modelName,
    config.modelPurpose,
    {
      ...options.constraints,
      focusAreas: config.focusAreas
    }
  );
}

export default {
  generateSystemPrompt,
  generateSystemPromptByUseCase
};
